// tabs/ModificariTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InsightsIcon from "@mui/icons-material/Insights";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const BASE_URL = "https://server-9p4hx.kinsta.app";

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDateParam = (date) =>
  date ? date.toISOString().split("T")[0] : null;

const createDefaultRange = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 29);
  return { start: startOfDay(start), end: endOfDay(today) };
};

const quickRangeOptions = [
  { key: "today", label: "Astăzi" },
  { key: "last7", label: "Ultimele 7 zile" },
  { key: "last30", label: "Ultimele 30 de zile" },
  { key: "currentMonth", label: "Luna curentă" },
  { key: "previousMonth", label: "Luna anterioară" },
];

const DateRangeInput = React.forwardRef(function DateRangeInput(
  { value, onClick, placeholder },
  ref,
) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Interval de date"
      value={value}
      onClick={onClick}
      inputRef={ref}
      placeholder={placeholder}
      InputProps={{ readOnly: true }}
    />
  );
});

function ModificariTab() {
  const defaultRange = useMemo(() => createDefaultRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [selectedQuickRange, setSelectedQuickRange] = useState("last30");
  const [modificariData, setModificariData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    field: "data",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const refreshPollerRef = React.useRef(null);
  const persistKey = "modificariTab.range";

  useEffect(() => {
    const raw = localStorage.getItem(persistKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const savedStart = parsed?.startDate ? new Date(parsed.startDate) : null;
      const savedEnd = parsed?.endDate ? new Date(parsed.endDate) : null;
      if (savedStart && !Number.isNaN(savedStart.getTime())) {
        setStartDate(startOfDay(savedStart));
      }
      if (savedEnd && !Number.isNaN(savedEnd.getTime())) {
        setEndDate(endOfDay(savedEnd));
      }
      if (parsed?.selectedQuickRange !== undefined) {
        setSelectedQuickRange(parsed.selectedQuickRange);
      }
    } catch (err) {
      console.warn("Nu am putut citi intervalul salvat:", err);
    }
  }, []);

  useEffect(() => {
    const payload = {
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      selectedQuickRange,
    };
    localStorage.setItem(persistKey, JSON.stringify(payload));
  }, [startDate, endDate, selectedQuickRange]);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogText, setExportDialogText] = useState("");
  const [exportLogMessages, setExportLogMessages] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  const actionButtonStyles = {
    textTransform: "none",
    borderRadius: 3,
    px: 2.5,
  };

  const formatCurrency = useMemo(
    () =>
      new Intl.NumberFormat("ro-RO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const formatDisplayDate = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const stripDiacritics = (value) =>
    value ? value.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : value;

  const getDiminuare = (row) => {
    const intrare = Number(row?.pret_intrare_ron);
    const actual = Number(row?.pret);
    if (Number.isNaN(intrare) || Number.isNaN(actual)) return null;
    return intrare - actual;
  };

  const stats = useMemo(() => {
    const grouped = new Map();
    modificariData.forEach((row) => {
      const key = row.issue_id || row.sku || row.nr_jurnal_special || row.id;
      const intrare =
        row?.pret_intrare_ron != null ? Number(row.pret_intrare_ron) : null;
      const actual = row?.pret != null ? Number(row.pret) : null;
      if (!key || intrare === null || Number.isNaN(intrare)) return;
      const current = grouped.get(key) || {
        intrare,
        minActual: Number.POSITIVE_INFINITY,
      };
      current.intrare = intrare;
      if (actual !== null && !Number.isNaN(actual)) {
        current.minActual = Math.min(current.minActual, actual);
      }
      grouped.set(key, current);
    });

    let sumaDiminuata = 0;
    grouped.forEach(({ intrare, minActual }) => {
      if (Number.isFinite(minActual)) {
        const diff = intrare - minActual;
        if (diff > 0) {
          sumaDiminuata += diff;
        }
      }
    });

    return {
      totalProduse: grouped.size,
      sumaDiminuata,
    };
  }, [modificariData]);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(modificariData)) return [];
    const dataCopy = [...modificariData];
    if (!sortConfig.field) return dataCopy;
    dataCopy.sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;
      const getValue = (row) => {
        switch (sortConfig.field) {
          case "nr_jurnal_special":
            return row.nr_jurnal_special || "";
          case "sku":
            return row.sku || "";
          case "tip_contract":
            return row.tip_contract || "";
          case "nume_si_prenume":
            return row.nume_si_prenume || "";
          case "summary":
            return row.summary || "";
          case "sursa_modificare":
            return row.sursa_modificare || "";
          case "data":
            return row.data ? new Date(row.data).getTime() : 0;
          case "pret_intrare_ron":
            return row.pret_intrare_ron != null
              ? Number(row.pret_intrare_ron)
              : -Infinity;
          case "pret":
            return row.pret != null ? Number(row.pret) : -Infinity;
          case "diminuare": {
            const diff = getDiminuare(row);
            return diff != null ? Number(diff) : -Infinity;
          }
          default:
            return "";
        }
      };
      const valueA = getValue(a);
      const valueB = getValue(b);
      if (typeof valueA === "number" && typeof valueB === "number") {
        return (valueA - valueB) * direction;
      }
      return (
        valueA.toString().localeCompare(valueB.toString(), "ro", {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
    return dataCopy;
  }, [modificariData, sortConfig]);

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { field, direction: "asc" };
    });
  };

  const fetchModificari = useCallback(async (rangeStart, rangeEnd) => {
    if (!rangeStart || !rangeEnd) return;
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE_URL}/api/tasks/modificari-taskuri`,
        {
          params: {
            de_la: formatDateParam(rangeStart),
            pana_la: formatDateParam(rangeEnd),
          },
        },
      );
      const sorted = Array.isArray(data)
        ? [...data].sort(
            (a, b) =>
              new Date(b.data || 0).getTime() - new Date(a.data || 0).getTime(),
          )
        : [];
      setModificariData(sorted);
    } catch (error) {
      console.error("Eroare la preluarea modificărilor:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModificari(startDate, endDate);
  }, [startDate, endDate, refreshKey, fetchModificari]);

  const handleRefreshTasks = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setRefreshProgress(5);

    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/tasks/refresh-tasks`,
        null,
        { params: { mode: "async" } },
      );
      const jobId = data?.jobId;
      if (!jobId) throw new Error("Nu am primit jobId de la server.");

      if (refreshPollerRef.current) {
        clearInterval(refreshPollerRef.current);
      }

      refreshPollerRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${BASE_URL}/api/tasks/refresh-tasks-status/${jobId}`,
          );
          const job = statusRes.data;
          if (typeof job.progress === "number") {
            setRefreshProgress(job.progress);
          }
          if (job.status === "done") {
            clearInterval(refreshPollerRef.current);
            refreshPollerRef.current = null;
            await fetchModificari(startDate, endDate);
            setToast({
              open: true,
              message: "Refresh finalizat. Datele sunt actualizate.",
              severity: "success",
            });
            setRefreshKey((prev) => prev + 1);
            setTimeout(() => {
              setIsRefreshing(false);
              setRefreshProgress(0);
            }, 300);
          } else if (job.status === "error") {
            clearInterval(refreshPollerRef.current);
            refreshPollerRef.current = null;
            setToast({
              open: true,
              message: job.error || "Refresh eșuat.",
              severity: "error",
            });
            setTimeout(() => {
              setIsRefreshing(false);
              setRefreshProgress(0);
            }, 300);
          }
        } catch (pollError) {
          console.error("Eroare la verificarea statusului:", pollError);
        }
      }, 1000);
    } catch (error) {
      console.error("Eroare la refresh:", error);
      setToast({
        open: true,
        message: "Refresh eșuat. Încearcă din nou.",
        severity: "error",
      });
      setIsRefreshing(false);
      setRefreshProgress(0);
    }
  };

  useEffect(() => {
    return () => {
      if (refreshPollerRef.current) {
        clearInterval(refreshPollerRef.current);
      }
    };
  }, []);

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setSelectedQuickRange("");
    setStartDate(start ? startOfDay(start) : null);
    setEndDate(end ? endOfDay(end) : null);
  };

  const handleClearFilters = () => {
    const freshRange = createDefaultRange();
    setStartDate(freshRange.start);
    setEndDate(freshRange.end);
    setSelectedQuickRange("last30");
  };

  const handleQuickRangeSelect = (rangeKey) => {
    const today = new Date();
    let range = null;

    switch (rangeKey) {
      case "today":
        range = { start: startOfDay(today), end: endOfDay(today) };
        break;
      case "last7": {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        range = { start: startOfDay(start), end: endOfDay(today) };
        break;
      }
      case "last30": {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        range = { start: startOfDay(start), end: endOfDay(today) };
        break;
      }
      case "currentMonth": {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        range = { start: startOfDay(start), end: endOfDay(end) };
        break;
      }
      case "previousMonth": {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        range = { start: startOfDay(start), end: endOfDay(end) };
        break;
      }
      default:
        break;
    }

    if (range) {
      setSelectedQuickRange(rangeKey);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  const exportRowsToExcel = (rows) => {
    if (!rows || !rows.length) return;
    const formatted = rows.map((row) => ({
      "Nr. Jurnal": row.nr_jurnal_special || "—",
      SKU: row.sku || "—",
      "Tip Contract": row.tip_contract || "—",
      "Nume și Prenume": row.nume_si_prenume || "—",
      Summary: row.summary || "—",
      "Data Modificare": formatDisplayDate(row.data),
      "Preț Intrare":
        row.pret_intrare_ron != null
          ? Number(row.pret_intrare_ron).toFixed(2)
          : "—",
      "Preț Actual": row.pret != null ? Number(row.pret).toFixed(2) : "—",
      "Diminuare (Intrare - Actual)": (() => {
        const diff = getDiminuare(row);
        return diff != null ? diff.toFixed(2) : "—";
      })(),
      Label: row.sursa_modificare || "—",
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formatted);
    XLSX.utils.book_append_sheet(wb, ws, "Modificări");
    XLSX.writeFile(wb, "Export_Modificari.xlsx");
  };

  const exportRowsToPDF = (rows) => {
    if (!rows || !rows.length) return;
    const doc = new jsPDF({ orientation: "landscape", format: "a4" });
    const headers = [
      "Nr. Jurnal",
      "SKU",
      "Tip Contract",
      "Nume și Prenume",
      "Summary",
      "Data",
      "Preț Intrare",
      "Preț Actual",
      "Diminuare",
      "Label",
    ];
    const body = rows.map((row) => [
      row.nr_jurnal_special || "—",
      row.sku || "—",
      row.tip_contract || "—",
      row.nume_si_prenume || "—",
      row.summary || "—",
      formatDisplayDate(row.data),
      row.pret_intrare_ron != null
        ? Number(row.pret_intrare_ron).toFixed(2)
        : "—",
      row.pret != null ? Number(row.pret).toFixed(2) : "—",
      (() => {
        const diff = getDiminuare(row);
        return diff != null ? diff.toFixed(2) : "—";
      })(),
      stripDiacritics(row.sursa_modificare || "—"),
    ]);
    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [58, 123, 213], textColor: 255 },
    });
    doc.save("Export_Modificari.pdf");
  };

  async function handleExportZipModificari() {
    setExportDialogText("Pregătim arhiva pentru export...");
    setExportDialogOpen(true);
    setIsSyncing(true);
    setExportLogMessages([]);
    const addLogMessage = (msg) =>
      setExportLogMessages((prev) => [...prev, msg]);

    try {
      if (!sortedRows || sortedRows.length === 0) {
        alert("Nu există date disponibile pentru export.");
        return;
      }

      const columns = [
        { label: "Nr Jurnal", field: "nr_jurnal_special" },
        { label: "SKU", field: "sku" },
        { label: "Tip Contract", field: "tip_contract" },
        { label: "Nume", field: "nume_si_prenume" },
        { label: "Summary", field: "summary" },
        { label: "Data", field: "data" },
        { label: "Pret Intrare", field: "pret_intrare_ron" },
        { label: "Pret Actual", field: "pret" },
        { label: "Diferenta", field: "diferenta" },
        { label: "Label", field: "sursa_modificare" },
      ];

      const rawData = sortedRows.map((row) => {
        const diferenta = getDiminuare(row);
        return {
          nr_jurnal_special: row.nr_jurnal_special ?? "",
          sku: row.sku ?? "",
          tip_contract: row.tip_contract ?? "",
          nume_si_prenume: row.nume_si_prenume ?? "",
          summary: row.summary ?? "",
          data: row.data ?? "",
          pret_intrare_ron:
            row.pret_intrare_ron != null ? row.pret_intrare_ron : "",
          pret: row.pret != null ? row.pret : "",
          diferenta: diferenta != null ? diferenta.toFixed(2) : "",
          sursa_modificare: row.sursa_modificare ?? "",
        };
      });

      const fileName = `Export Modificari`;

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rawData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBlob = new Blob(
        [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
        { type: "application/octet-stream" },
      );

      const doc = new jsPDF();
      const headers = [columns.map((col) => col.label)];
      const body = rawData.map((row) =>
        columns.map((col) => {
          if (col.field === "sursa_modificare") {
            return stripDiacritics(row[col.field] || "");
          }
          return row[col.field];
        }),
      );
      autoTable(doc, { head: headers, body });
      const pdfBlob = doc.output("blob");

      const toBase64 = (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const [excelBase64, pdfBase64] = await Promise.all([
        toBase64(excelBlob),
        toBase64(pdfBlob),
      ]);

      const tasks = sortedRows.map((row) => ({
        issue_id: row.issue_id,
        nr_jurnal_special: row.nr_jurnal_special,
        nume_si_prenume: row.nume_si_prenume,
        tip_achizitie: row.tip_contract || "",
        attachments: row.attachments || "",
      }));

      const payload = {
        tip_achizitie: "Modificari",
        luna: new Date().toLocaleString("ro-RO", {
          month: "long",
          year: "numeric",
        }),
        tasks,
        rawData,
        columns,
        fileName,
        pdfBuffer: pdfBase64,
        excelBuffer: excelBase64,
      };

      addLogMessage("Trimitere date către server...");
      setExportDialogText("Trimitere date către server...");

      const response = await fetch(`${BASE_URL}/api/tasks/export-zip-server`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Export ZIP a eșuat");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Export_Modificari.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportDialogText("Export finalizat cu succes!");
      setTimeout(() => setExportDialogOpen(false), 3000);
    } catch (err) {
      setExportDialogText("Eroare la export: " + err.message);
      setTimeout(() => setExportDialogOpen(false), 3500);
    } finally {
      setIsSyncing(false);
    }
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(135deg, #1d2671 0%, #c33764 100%)",
          color: "common.white",
          boxShadow: "0px 25px 60px rgba(23, 32, 90, 0.35)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Panou Modificări Preț
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Vizualizează produsele ajustate, monitorizează diminuarea valorii
              și exportă rapoarte complete.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => exportRowsToExcel(sortedRows)}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(255,255,255,0.22)",
                color: "#fff",
              }}
            >
              Export Excel
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => exportRowsToPDF(sortedRows)}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(255,255,255,0.22)",
                color: "#fff",
              }}
            >
              Export PDF
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportZipModificari}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(0,0,0,0.25)",
                color: "#fff",
              }}
            >
              Export Zip
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefreshTasks}
              sx={{
                ...actionButtonStyles,
                bgcolor: "rgba(0,0,0,0.35)",
                color: "#fff",
              }}
              disabled={isRefreshing}
            >
              Reîncarcă datele
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box sx={{ width: { xs: "100%", sm: 320 } }}>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Interval Modificări
            </Typography>
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleRangeChange}
              shouldCloseOnSelect={false}
              dateFormat="dd MMM yyyy"
              isClearable
              customInput={
                <DateRangeInput placeholder="Selectează intervalul dorit" />
              }
            />
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1,
                flexWrap: "nowrap",
              }}
            >
              {quickRangeOptions.map((option) => (
                <Chip
                  key={option.key}
                  label={option.label}
                  onClick={() => handleQuickRangeSelect(option.key)}
                  color={
                    selectedQuickRange === option.key ? "primary" : "default"
                  }
                  variant={
                    selectedQuickRange === option.key ? "filled" : "outlined"
                  }
                  sx={{ flex: 1 }}
                />
              ))}
            </Box>
          </Box>
          <Button variant="text" onClick={handleClearFilters}>
            Resetează filtrele
          </Button>
        </Stack>
        {isRefreshing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Se reîmprospătează datele din server...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={refreshProgress}
              sx={{ height: 8, borderRadius: 5 }}
            />
          </Box>
        )}
      </Paper>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2,
              background: "linear-gradient(135deg, #f7971e, #ffd200)",
              color: "common.white",
              boxShadow: "0px 20px 45px rgba(247,151,30,0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <Inventory2Icon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Total produse afectate
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalProduse}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Produse afectate în perioada aleasă
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2,
              background: "linear-gradient(135deg, #ff416c, #ff4b2b)",
              color: "common.white",
              boxShadow: "0px 20px 45px rgba(255,65,108,0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <TrendingDownIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Suma diminuată
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {formatCurrency.format(stats.sumaDiminuata || 0)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Calcul: Intrare - cel mai mic preț actual
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.1)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.100" }}>
              <TableCell
                sortDirection={
                  sortConfig.field === "nr_jurnal_special"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "nr_jurnal_special"}
                  direction={
                    sortConfig.field === "nr_jurnal_special"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("nr_jurnal_special")}
                >
                  Nr. Jurnal
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "sku" ? sortConfig.direction : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "sku"}
                  direction={
                    sortConfig.field === "sku" ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort("sku")}
                >
                  SKU
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "tip_contract"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "tip_contract"}
                  direction={
                    sortConfig.field === "tip_contract"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("tip_contract")}
                >
                  Tip Contract
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "nume_si_prenume"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "nume_si_prenume"}
                  direction={
                    sortConfig.field === "nume_si_prenume"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("nume_si_prenume")}
                >
                  Nume și Prenume
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "summary" ? sortConfig.direction : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "summary"}
                  direction={
                    sortConfig.field === "summary"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("summary")}
                >
                  Summary
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "data" ? sortConfig.direction : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "data"}
                  direction={
                    sortConfig.field === "data" ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort("data")}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "pret_intrare_ron"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "pret_intrare_ron"}
                  direction={
                    sortConfig.field === "pret_intrare_ron"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("pret_intrare_ron")}
                >
                  Preț Intrare
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "pret" ? sortConfig.direction : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "pret"}
                  direction={
                    sortConfig.field === "pret" ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort("pret")}
                >
                  Preț Actual
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "diminuare"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "diminuare"}
                  direction={
                    sortConfig.field === "diminuare"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("diminuare")}
                >
                  Diminuare
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  sortConfig.field === "sursa_modificare"
                    ? sortConfig.direction
                    : false
                }
              >
                <TableSortLabel
                  active={sortConfig.field === "sursa_modificare"}
                  direction={
                    sortConfig.field === "sursa_modificare"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("sursa_modificare")}
                >
                  Label
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Stack spacing={1} alignItems="center" py={3}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Se încarcă modificările...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    Nu există modificări pentru intervalul selectat.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => {
                const diminuare = getDiminuare(row);
                return (
                  <TableRow
                    key={`${row.issue_id}-${index}`}
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "grey.50" },
                      "&:hover": { backgroundColor: "rgba(33,150,243,0.08)" },
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <TableCell>{row.nr_jurnal_special || "—"}</TableCell>
                    <TableCell>{row.sku || "—"}</TableCell>
                    <TableCell>{row.tip_contract || "—"}</TableCell>
                    <TableCell>{row.nume_si_prenume || "—"}</TableCell>
                    <TableCell>{row.summary || "—"}</TableCell>
                    <TableCell>{formatDisplayDate(row.data)}</TableCell>
                    <TableCell>
                      {row.pret_intrare_ron != null
                        ? formatCurrency.format(Number(row.pret_intrare_ron))
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {row.pret != null
                        ? formatCurrency.format(Number(row.pret))
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {diminuare != null
                        ? formatCurrency.format(diminuare)
                        : "—"}
                    </TableCell>
                    <TableCell>{row.sursa_modificare || "—"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isSyncing && (
        <Box sx={{ width: "100%", mx: 2 }}>
          <Typography variant="body2" align="center" mt={1}>
            Procesăm exportul...
          </Typography>
        </Box>
      )}

      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Export în curs</DialogTitle>
        <DialogContent dividers>
          <Typography>{exportDialogText}</Typography>

          {exportDialogText === "Export finalizat cu succes!" && (
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
              <Typography variant="h6" color="success.main">
                Export realizat cu succes!
              </Typography>
            </Box>
          )}

          <Box mt={2} sx={{ maxHeight: 200, overflowY: "auto" }}>
            {(exportLogMessages || []).map((msg, idx) => (
              <Typography key={idx} variant="body2">
                • {msg}
              </Typography>
            ))}
          </Box>

          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              Exportul este în curs. Te rugăm să aștepți câteva secunde...
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="primary">
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ModificariTab;
